import { useEffect, useMemo, useState } from "react";
import { Loader2, Megaphone } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";

import { announcementsApi } from "@/lib/announcements-api";
import type {
  AnnouncementAudienceType,
  CreateAnnouncementBody,
} from "@/lib/announcements-api";
import type { NotificationSeverity } from "@/lib/notifications-api";
import { employeesApi } from "@/lib/employees-api";
import { sitesApi } from "@/lib/sites-api";
import { departmentsApi } from "@/lib/departments-api";
import { ApiError } from "@/lib/api";
import { fetchAllPages } from "@/lib/paging";

type ComposerEmployee = {
  id: string;
  name: string;
  empCode: string;
  departmentId: string | null;
  department: string | null;
};

// The composer shows every employee / site the tenant has, not just the first
// page — the backend caps `pageSize` at 200 (see PaginationDto.@Max).
async function fetchAllEmployees(): Promise<ComposerEmployee[]> {
  const rows = await fetchAllPages((page, pageSize) => employeesApi.list({ page, pageSize }));
  return rows.map((e) => ({ id: e.id, name: e.name, empCode: e.empCode, departmentId: e.departmentId, department: e.department }));
}

async function fetchAllSites(): Promise<Array<{ id: string; name: string; code: string }>> {
  const rows = await fetchAllPages((page, pageSize) => sitesApi.list({ page, pageSize }));
  return rows.map((s) => ({ id: s.id, name: s.name, code: s.code }));
}

/**
 * Subtree-aware reach estimate: an employee is reached when their unit is one
 * of the selected nodes or lies anywhere inside a selected node's subtree —
 * mirroring the backend fan-out (BR-076).
 */
function countSubtreeReach(
  employees: ComposerEmployee[],
  departments: Array<{ id: string; path: string }>,
  selectedIds: string[],
): number {
  const pathById = new Map(departments.map((d) => [d.id, d.path]));
  const selectedPaths = selectedIds
    .map((id) => pathById.get(id))
    .filter((p): p is string => !!p);
  return employees.filter((e) => {
    if (!e.departmentId) return false;
    const p = pathById.get(e.departmentId);
    return !!p && selectedPaths.some((sp) => p === sp || p.startsWith(sp + "/"));
  }).length;
}

/**
 * Dialog for composing an announcement — one place, used by both the
 * notifications page and (later) the dashboard. Renders a dynamic audience
 * picker that swaps controls based on the selected audience type.
 */
export function AnnouncementComposer({
  open,
  onOpenChange,
  onSent,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSent?: () => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState<NotificationSeverity>("INFO");
  const [audienceType, setAudienceType] = useState<AnnouncementAudienceType>("ALL_EMPLOYEES");
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [employeeSearch, setEmployeeSearch] = useState("");

  const [employees, setEmployees] = useState<ComposerEmployee[]>([]);
  const [sites, setSites] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Lazy-load the audience source data the first time the dialog opens.
  // Employees and sites are both paginated server-side (pageSize cap = 200),
  // so we loop through pages until we've pulled everything. For tenants with
  // ~1–5k employees this is 1–25 sequential calls on dialog open; cheap
  // enough given we cache the result for subsequent opens.
  useEffect(() => {
    if (!open) return;
    if (employees.length > 0 && sites.length > 0) return;
    let cancelled = false;
    (async () => {
      setSourceLoading(true);
      try {
        const [emp, site, deps] = await Promise.all([
          fetchAllEmployees(),
          fetchAllSites(),
          departmentsApi.listAll(),
        ]);
        if (cancelled) return;
        setEmployees(emp);
        setSites(site);
        setDepartments(
          deps.map((d) => ({
            id: d.id,
            label: d.pathNames?.length ? d.pathNames.join(" › ") : d.name,
            path: d.path,
          })),
        );
      } catch (e) {
        if (!cancelled) {
          toast.error(
            e instanceof ApiError ? e.message : "Failed to load audience data",
          );
        }
      } finally {
        if (!cancelled) setSourceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, employees.length, sites.length]);

  // Managed org units (ADR-0008/0010) — audience.departments carries their
  // IDs; the backend fans out to each unit's whole subtree (BR-076).
  const [departments, setDepartments] = useState<
    Array<{ id: string; label: string; path: string }>
  >([]);

  const filteredEmployees = useMemo(() => {
    const q = employeeSearch.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.empCode.toLowerCase().includes(q) ||
        (e.department ?? "").toLowerCase().includes(q),
    );
  }, [employees, employeeSearch]);

  const reset = () => {
    setTitle("");
    setBody("");
    setSeverity("INFO");
    setAudienceType("ALL_EMPLOYEES");
    setSelectedDepartments([]);
    setSelectedSiteIds([]);
    setSelectedEmployeeIds([]);
    setEmployeeSearch("");
  };

  const validate = (): string | null => {
    if (title.trim().length < 2) return "Title is required";
    if (body.trim().length < 2) return "Body is required";
    if (audienceType === "DEPARTMENT" && selectedDepartments.length === 0)
      return "Pick at least one department";
    if (audienceType === "SITE" && selectedSiteIds.length === 0)
      return "Pick at least one site";
    if (audienceType === "SPECIFIC_EMPLOYEES" && selectedEmployeeIds.length === 0)
      return "Pick at least one employee";
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const payload: CreateAnnouncementBody = {
      title: title.trim(),
      body: body.trim(),
      severity,
      audience: { type: audienceType },
    };
    if (audienceType === "DEPARTMENT") payload.audience.departments = selectedDepartments;
    if (audienceType === "SITE") payload.audience.siteIds = selectedSiteIds;
    if (audienceType === "SPECIFIC_EMPLOYEES")
      payload.audience.employeeIds = selectedEmployeeIds;

    setSubmitting(true);
    try {
      const res = await announcementsApi.send(payload);
      toast.success(`Announcement sent to ${res.recipients} recipient${res.recipients === 1 ? "" : "s"}`);
      reset();
      onSent?.();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Failed to send announcement");
    } finally {
      setSubmitting(false);
    }
  };

  const estimatedReach = useMemo(() => {
    switch (audienceType) {
      case "ALL_EMPLOYEES":
        return `All ${employees.length} active employees`;
      case "ALL_ADMINS":
        return "All admin users in the tenant";
      case "DEPARTMENT":
        return selectedDepartments.length === 0
          ? "Pick departments to see reach"
          : `${countSubtreeReach(employees, departments, selectedDepartments)} employees`;
      case "SITE":
        return selectedSiteIds.length === 0
          ? "Pick sites to see reach"
          : `${selectedSiteIds.length} site${selectedSiteIds.length === 1 ? "" : "s"} selected`;
      case "SPECIFIC_EMPLOYEES":
        return `${selectedEmployeeIds.length} employee${selectedEmployeeIds.length === 1 ? "" : "s"} selected`;
    }
  }, [audienceType, employees, departments, selectedDepartments, selectedSiteIds, selectedEmployeeIds]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v && !submitting) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            Send announcement
          </DialogTitle>
          <DialogDescription>
            Push a notification to employees or admins. They'll see it in the mobile app's
            notifications tab (or here, for admins).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ann-title">Title</Label>
            <Input
              id="ann-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Office closed Friday"
              maxLength={120}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ann-body">Message</Label>
            <Textarea
              id="ann-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="The full announcement text your team will see."
              rows={4}
              maxLength={2000}
            />
            <div className="text-right text-[10px] text-muted-foreground">
              {body.length} / 2000
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Severity</Label>
              <Select
                value={severity}
                onValueChange={(v) => setSeverity(v as NotificationSeverity)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INFO">Info</SelectItem>
                  <SelectItem value="WARNING">Warning</SelectItem>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Audience</Label>
              <Select
                value={audienceType}
                onValueChange={(v) => setAudienceType(v as AnnouncementAudienceType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL_EMPLOYEES">All employees</SelectItem>
                  <SelectItem value="ALL_ADMINS">All admins</SelectItem>
                  <SelectItem value="DEPARTMENT">Specific departments</SelectItem>
                  <SelectItem value="SITE">Specific sites</SelectItem>
                  <SelectItem value="SPECIFIC_EMPLOYEES">Specific employees</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {audienceType === "DEPARTMENT" && (
            <AudienceList
              loading={sourceLoading}
              emptyText="No departments found"
              items={departments.map((d) => ({ id: d.id, label: d.label }))}
              selected={selectedDepartments}
              onToggle={(id) =>
                setSelectedDepartments((p) =>
                  p.includes(id) ? p.filter((x) => x !== id) : [...p, id],
                )
              }
            />
          )}

          {audienceType === "SITE" && (
            <AudienceList
              loading={sourceLoading}
              emptyText="No sites found"
              items={sites.map((s) => ({ id: s.id, label: `${s.name} · ${s.code}` }))}
              selected={selectedSiteIds}
              onToggle={(id) =>
                setSelectedSiteIds((p) =>
                  p.includes(id) ? p.filter((x) => x !== id) : [...p, id],
                )
              }
            />
          )}

          {audienceType === "SPECIFIC_EMPLOYEES" && (
            <div className="space-y-2">
              <Input
                placeholder="Search by name, emp code, or department…"
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
              />
              <AudienceList
                loading={sourceLoading}
                emptyText="No employees match"
                items={filteredEmployees.map((e) => ({
                  id: e.id,
                  label: `${e.name} · ${e.empCode}${e.department ? ` · ${e.department}` : ""}`,
                }))}
                selected={selectedEmployeeIds}
                onToggle={(id) =>
                  setSelectedEmployeeIds((p) =>
                    p.includes(id) ? p.filter((x) => x !== id) : [...p, id],
                  )
                }
              />
            </div>
          )}

          <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <Megaphone className="h-3.5 w-3.5 shrink-0" />
            <span>Reach: {estimatedReach}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Sending…
              </>
            ) : (
              "Send announcement"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AudienceList({
  items,
  selected,
  onToggle,
  loading,
  emptyText,
}: {
  items: Array<{ id: string; label: string }>;
  selected: string[];
  onToggle: (id: string) => void;
  loading: boolean;
  emptyText: string;
}) {
  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center rounded-md border text-xs text-muted-foreground">
        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center rounded-md border text-xs text-muted-foreground">
        {emptyText}
      </div>
    );
  }
  return (
    <ScrollArea className="h-48 rounded-md border">
      <div className="divide-y">
        {items.map((it) => {
          const checked = selected.includes(it.id);
          return (
            <label
              key={it.id}
              className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-secondary/30"
            >
              <Checkbox checked={checked} onCheckedChange={() => onToggle(it.id)} />
              <span className="truncate">{it.label}</span>
            </label>
          );
        })}
      </div>
    </ScrollArea>
  );
}
